// src/pages/PublicFormPage.tsx
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  updateDoc,
  increment,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "@/lib/firebase";
import type { Form } from "@/types/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function PublicFormPage() {
  const { formId } = useParams<{ formId: string }>();
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [files, setFiles] = useState<Record<string, FileList | null>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Загружаем форму
  useEffect(() => {
    if (!formId) {
      setError("Форма не найдена");
      setLoading(false);
      return;
    }

    const loadForm = async () => {
      try {
        const snap = await getDoc(doc(db, "forms", formId));
        if (!snap.exists()) {
          setError("Форма не найдена");
          setLoading(false);
          return;
        }

        const data = { id: snap.id, ...snap.data() } as Form;

        if (data.status !== "active") {
          setError(
            data.status === "draft"
              ? "Эта форма ещё не опубликована"
              : "Приём ответов по этой форме закрыт"
          );
          setLoading(false);
          return;
        }

        setForm(data);

        // Инициализируем пустые ответы
        const initial: Record<string, any> = {};
        data.fields?.forEach((f) => {
          if (f.type === "checkbox") initial[f.id] = [];
          else if (f.type === "file") initial[f.id] = [];
          else initial[f.id] = "";
        });
        setAnswers(initial);
      } catch (e) {
        console.error(e);
        setError("Не удалось загрузить форму");
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [formId]);

  const handleChange = (fieldId: string, value: any) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleCheckbox = (fieldId: string, option: string, checked: boolean) => {
    setAnswers((prev) => {
      const current: string[] = prev[fieldId] || [];
      if (checked) {
        return { ...prev, [fieldId]: [...current, option] };
      } else {
        return { ...prev, [fieldId]: current.filter((v) => v !== option) };
      }
    });
  };

  const handleFileChange = (fieldId: string, fileList: FileList | null) => {
    setFiles((prev) => ({ ...prev, [fieldId]: fileList }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;

    // Валидация обязательных полей
    for (const field of form.fields || []) {
      if (field.type === "section") continue;

      if (field.required) {
        if (field.type === "file") {
          const fileList = files[field.id];
          if (!fileList || fileList.length === 0) {
            alert(`Прикрепите файл к полю: ${field.label}`);
            return;
          }
        } else {
          const val = answers[field.id];
          if (
            val === undefined ||
            val === null ||
            val === "" ||
            (Array.isArray(val) && val.length === 0)
          ) {
            alert(`Заполните обязательное поле: ${field.label}`);
            return;
          }
        }
      }
    }

    setSubmitting(true);

    try {
      // Копируем ответы, чтобы добавить в них ссылки на файлы
      const finalAnswers: Record<string, any> = { ...answers };

      // Загружаем все файлы
      for (const field of form.fields || []) {
        if (field.type !== "file") continue;

        const fileList = files[field.id];
        if (!fileList || fileList.length === 0) {
          finalAnswers[field.id] = [];
          continue;
        }

        const uploadedUrls: string[] = [];

        for (const file of Array.from(fileList)) {
          const timestamp = Date.now();
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const storagePath = `forms/${form.id}/responses/${timestamp}_${safeName}`;

          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          uploadedUrls.push(url);
        }

        finalAnswers[field.id] = uploadedUrls;
      }

      // Сохраняем ответ
      await addDoc(collection(db, "formResponses"), {
        formId: form.id,
        answers: finalAnswers,
        submittedAt: Timestamp.now(),
        meta: {
          userAgent: navigator.userAgent,
        },
      });

      // Увеличиваем счётчик
      try {
        await updateDoc(doc(db, "forms", form.id), {
          responsesCount: increment(1),
        });
      } catch (counterError) {
        console.warn("Не удалось обновить счётчик:", counterError);
      }

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert("Ошибка при отправке. Попробуйте ещё раз.");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== СОСТОЯНИЯ =====

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-purple-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Ошибка</h1>
          <p className="text-purple-300 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 flex items-center justify-center p-6">
        <div className="text-center max-w-lg">
          <CheckCircle2 className="h-20 w-20 text-green-400 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-white mb-4">Спасибо!</h1>
          <p className="text-purple-200 text-xl">
            {form?.settings?.thankYouMessage || "Ваш отчёт успешно принят."}
          </p>
        </div>
      </div>
    );
  }

  if (!form) return null;

  // ===== РЕНДЕР ФОРМЫ =====

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Шапка */}
        <div className="text-center mb-10">
          <img
            src="/images/logo.png"
            alt="ОРВ ВСКС"
            className="h-20 mx-auto mb-6 drop-shadow-xl"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {form.title}
          </h1>
          {form.description && (
            <p className="text-purple-300 text-lg">{form.description}</p>
          )}
        </div>

        {/* Форма */}
        <form
          onSubmit={handleSubmit}
          className="bg-purple-950/40 border border-purple-700/50 rounded-3xl p-6 md:p-10 backdrop-blur-xl shadow-2xl space-y-8"
        >
          {form.fields?.map((field) => (
            <div key={field.id}>
              {field.type === "section" ? (
                <h2 className="text-2xl font-bold text-purple-200 border-b border-purple-700 pb-3 mt-4">
                  {field.label}
                </h2>
              ) : (
                <div className="space-y-2">
                  <Label className="text-purple-100 text-base">
                    {field.label}
                    {field.required && (
                      <span className="text-red-400 ml-1">*</span>
                    )}
                  </Label>

                  {/* Текст / телефон / email / ссылка */}
                  {(field.type === "text" ||
                    field.type === "phone" ||
                    field.type === "email" ||
                    field.type === "link" ||
                    field.type === "vk_link") && (
                    <Input
                      type={
                        field.type === "email"
                          ? "email"
                          : field.type === "phone"
                          ? "tel"
                          : "text"
                      }
                      value={answers[field.id] || ""}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      placeholder={field.placeholder || ""}
                      className="bg-slate-900/70 border-purple-600 text-white"
                      required={field.required}
                    />
                  )}

                  {/* Длинный текст */}
                  {field.type === "textarea" && (
                    <Textarea
                      value={answers[field.id] || ""}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      placeholder={field.placeholder || ""}
                      className="bg-slate-900/70 border-purple-600 text-white min-h-[100px]"
                      required={field.required}
                    />
                  )}

                  {/* Число */}
                  {field.type === "number" && (
                    <Input
                      type="number"
                      value={answers[field.id] || ""}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      placeholder={field.placeholder || ""}
                      className="bg-slate-900/70 border-purple-600 text-white"
                      required={field.required}
                    />
                  )}

                  {/* Дата */}
                  {field.type === "date" && (
                    <Input
                      type="date"
                      value={answers[field.id] || ""}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      className="bg-slate-900/70 border-purple-600 text-white"
                      required={field.required}
                    />
                  )}

                  {/* Выпадающий список */}
                  {field.type === "select" && (
                    <select
                      value={answers[field.id] || ""}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      className="w-full h-10 rounded-md bg-slate-900/70 border border-purple-600 text-white px-3"
                      required={field.required}
                    >
                      <option value="">Выберите...</option>
                      {(field.options || []).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Радиокнопки */}
                  {field.type === "radio" && (
                    <div className="space-y-2">
                      {(field.options || []).map((opt) => (
                        <label
                          key={opt}
                          className="flex items-center gap-3 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name={field.id}
                            value={opt}
                            checked={answers[field.id] === opt}
                            onChange={() => handleChange(field.id, opt)}
                            className="w-4 h-4 accent-purple-500"
                          />
                          <span className="text-purple-100">{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Чекбоксы */}
                  {field.type === "checkbox" && (
                    <div className="space-y-2">
                      {(field.options || []).map((opt) => (
                        <label
                          key={opt}
                          className="flex items-center gap-3 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={(answers[field.id] || []).includes(opt)}
                            onChange={(e) =>
                              handleCheckbox(field.id, opt, e.target.checked)
                            }
                            className="w-4 h-4 accent-purple-500"
                          />
                          <span className="text-purple-100">{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* ===== ЗАГРУЗКА ФАЙЛОВ ===== */}
                  {field.type === "file" && (
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) =>
                          handleFileChange(field.id, e.target.files)
                        }
                        className="bg-slate-900/70 border-purple-600 text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-600 file:text-white"
                      />
                      {files[field.id] && files[field.id]!.length > 0 && (
                        <p className="text-sm text-purple-300">
                          Выбрано файлов: {files[field.id]!.length}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-lg py-6 rounded-2xl"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Отправка...
              </>
            ) : (
              "Отправить отчёт"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}